import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, RefObject } from 'react';
import { useRef } from 'react';
import {
  MARK_HIGHLIGHT,
  MARK_LINK,
  MARK_STYLED,
  MARK_TEXT_STYLE,
  NODE_BR,
  NODE_HEADING,
  NODE_PARAGRAPH,
  render,
} from 'storyblok-rich-text-react-renderer';
import { useTransitionController } from 'util/react-transition-component';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import useThemeScrollBlock from '../../../hooks/useThemeScrollBlock';
import PrimaryLink from '../../atoms/primary-link/PrimaryLink';
import Text from '../../atoms/text/Text';
import Heading from '../../atoms/heading/Heading';
import type { HeadingAs, HeadingSize } from '../../atoms/heading/Heading.data';
import type { RichTextProps } from './RichText.data';
import styles from './RichText.module.scss';
import { setupTransitionInTimeline, setupTransitionOutTimeline } from './RichText.transitions';
import { useIsDesktopDevice } from '../../../hooks/useIsDesktopDevice';

import DynamicComponent from '../../DynamicComponent';
import type { HeadingStoryblok } from '../../../util/storyblok/types/global.types';

export default function RichText({
  isVisible,
  className,
  children,
  blok,
  canTransitionIn,
  ref,
}: RichTextProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const isDesktopDevice = useIsDesktopDevice(false);

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  const mapHeading = (level: number): string => {
    if (level === 6) {
      return 'label-small';
    }

    return `heading-0${level}`;
  };

  useChangeTransitionState(transitionController, isVisible);
  useThemeScrollBlock(elementRef, blok.use_accent_color);

  if (blok.text.content.length <= 1) {
    if (blok.text.content.length === 0 || !blok.text.content?.[0].content) {
      return <div ref={elementRef} {...sbEditable(blok)} />;
    }
  }

  return (
    <>
      <div
        ref={elementRef}
        className={classNames(styles.richText, className)}
        {...sbEditable(blok)}
      >
        {render(blok.text, {
          markResolvers: {
            [MARK_STYLED]: (children) => <>{children}</>,
            [MARK_LINK]: (children, props) => {
              let { target } = props;
              if (props.href?.startsWith('http')) {
                target = '_blank';
              }
              return (
                <PrimaryLink
                  link={{ href: props.href! }}
                  label={children as string}
                  className={classNames(styles.link, 'link-underline-decoration')}
                  target={(target as '_blank') || undefined}
                />
              );
            },
            // Probably needs to be updated to children.map(child => <span style={style}>{children}</span>)
            // the day we actually need colors and highlights but currently it doesnt cooperate well
            // with the text split
            [MARK_TEXT_STYLE]: (children) => <>{children}</>,
            [MARK_HIGHLIGHT]: (children) => <>{children}</>,
          },
          nodeResolvers: {
            [NODE_HEADING]: (children, { level }) => {
              let result = children;
              // @ts-ignore
              if (children?.[0]) {
                // @ts-ignore
                if (typeof children[0] === 'object') {
                  // sometimes Storyblok sends a React component instead of a string which causes
                  // splitText to make it all in one line regardless of line breaks.
                  // So we do the following to extract the string from the component
                  // @ts-ignore
                  result = children[0]?.props?.children ?? children[0];
                }
              }
              return (
                <Heading
                  as={`h${level}` as HeadingAs}
                  className={classNames(styles[mapHeading(level)])}
                  size={mapHeading(level) as HeadingSize}
                  transitionInType={!isDesktopDevice ? 'none' : 'splitLineRotateInRichText'}
                  disableTriggerInOnScroll={false}
                  canTransitionIn={canTransitionIn}
                >
                  {result}
                </Heading>
              );
            },
            [NODE_PARAGRAPH]: (children) => (
              <Text
                className={styles.paragraph}
                as="p"
                size="body-large"
                transitionInType={!isDesktopDevice ? 'none' : 'splitCharsWordsRichText'}
                disableTriggerInOnScroll={false}
                canTransitionIn={canTransitionIn}
              >
                {children}
              </Text>
            ),

            [NODE_BR]: () => (
              <span className={styles.spacer}>
                <br />
              </span>
            ),
          },
          blokResolvers: {
            heading: (props: Record<string, unknown>) => {
              const { display_size, size, text } = props as HeadingStoryblok;
              if (!text || !display_size) return null;
              return (
                <Heading size={display_size} as={size} disableTriggerInOnScroll={false}>
                  {text}
                </Heading>
              );
            },
          },
          defaultBlokResolver: (name, props) => {
            const blok = { ...props, component: name };
            return <DynamicComponent blok={blok} key={blok._uid} />;
          },
        })}
        {children}
      </div>
    </>
  );
}
